// Eskom PARCC — Lumen header (sticky, translucent).
//
// Collapse toggle · page title + description · Search (opens the palette) ·
// notification bell · Settings · light/dark · avatar (account) · Sign out.
//
// Every one of these exists in Legacy too — the bell, the theme toggle, account,
// change password and sign out are the same components; only the arrangement and
// the Search entry point are Lumen chrome.
import { Link, useLocation } from 'react-router-dom';
import { PanelLeft, Search, Settings, Sun, Moon, LogOut, Sparkles } from 'lucide-react';
import { useAuth } from '../../../lib/auth';
import { useTheme } from '../../../lib/theme';
import { canAccessRoute } from '../../../lib/permissions';
import { NotificationBell } from '../NotificationBell';
import { routeMeta } from '../routes';

function initialsOf(name: string): string {
  const parts = name
    .replace(/[._-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function LumenHeader({
  onToggleSidebar,
  onOpenPalette,
  onOpenAccount,
}: {
  onToggleSidebar: () => void;
  onOpenPalette: () => void;
  onOpenAccount: () => void;
}): React.JSX.Element {
  const { user, role, grant, signOutUser } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const meta = routeMeta(location.pathname);
  const displayName = user?.displayName?.trim() || user?.email?.split('@')[0] || 'Account';

  return (
    <header className="lumen-header">
      <button
        type="button"
        className="lumen-iconbtn sq36"
        onClick={onToggleSidebar}
        aria-label="Toggle sidebar"
        title="Toggle sidebar"
      >
        <PanelLeft size={18} aria-hidden />
      </button>

      {/* The description is a hover tooltip on the title rather than a second
          always-on line — it is context, not something you read every visit, and
          dropping it gives the header back a row of breathing space. */}
      <div className="lumen-title">
        <h1 title={meta.desc}>{meta.title}</h1>
      </div>

      <button
        type="button"
        className="lumen-searchbtn"
        onClick={onOpenPalette}
        aria-label="Search"
        title="Search (press /)"
      >
        <Search size={16} aria-hidden />
        <span>Search</span>
        <kbd className="lumen-kbd">/</kbd>
      </button>

      <NotificationBell />

      <Link to="/settings" className="lumen-iconbtn" aria-label="Settings" title="Settings">
        <Settings size={18} aria-hidden />
      </Link>

      {/* Ask AI — the second entry point to the workspace, alongside the sidebar
          item and the command palette.

          GATED ON canAccessRoute, NOT on useAiAvailability().available. All
          three entry points must agree, and the route's own gate is eligibility
          (AccessGrant.aiEnabled) — not the AI Mode preference, which lives in
          localStorage and only decides what somebody SEES. Gating a navigation
          control on a preference would also hide the button from exactly the
          person who needs to reach the page that tells them to switch AI Mode
          on (AiWorkspacePage renders that message itself). */}
      {canAccessRoute('aiworkspace', grant) && (
        <Link to="/aiworkspace" className="lumen-askbtn" aria-label="Ask AI" title="Ask AI">
          <Sparkles size={17} aria-hidden />
        </Link>
      )}

      <button
        type="button"
        className="lumen-iconbtn"
        onClick={toggle}
        aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        aria-pressed={theme === 'light'}
        title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
      >
        {theme === 'dark' ? <Sun size={17} aria-hidden /> : <Moon size={17} aria-hidden />}
      </button>

      <span className="lumen-headsep" aria-hidden />

      <button
        type="button"
        className="lumen-avatarbtn"
        onClick={onOpenAccount}
        aria-label="My account"
        title={`${displayName}${role ? ` · ${role}` : ''}`}
      >
        {user?.photoURL ? (
          <img
            src={user.photoURL}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          initialsOf(displayName)
        )}
      </button>

      {/* Icon-only per the handoff. `aria-label` is load-bearing, not decoration:
          it IS the accessible name once the text is gone, and the theme-parity
          test matches header capabilities by accessible name. */}
      <button
        type="button"
        className="lumen-iconbtn lumen-signout"
        onClick={() => void signOutUser()}
        aria-label="Sign out"
        title="Sign out"
      >
        <LogOut size={17} aria-hidden />
      </button>
    </header>
  );
}
